import { useEffect, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import type { AppointmentWithThumbnail } from '@bb/domain/dist/types';
import errorReporter from '@app/errorReporter';
import { message } from 'components';
import { useDebounce } from 'hooks';
import { featureFlagClient } from 'utils/featureFlagBrowserClient';
import type { VirtualTourWithThumbnail } from '../types';

const useListingAppointments = () => {
  const [isListingAppointmentsLoading, setIsListingAppointmentsLoading] =
    useState(false);
  const [listingAppointments, setListingAppointments] = useState<
    (AppointmentWithThumbnail | VirtualTourWithThumbnail)[]
  >([]);
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchValue = useDebounce(searchValue, 500);

  const fetchAppointments = async () => {
    setIsListingAppointmentsLoading(true);
    try {
      const chronologicalShowingsEnabled = featureFlagClient.featureEnabled(
        'chronologicalShowings',
      );

      const {
        data: { appointments: listingAppt },
      } = await axios.get<{
        appointments: AppointmentWithThumbnail[];
        count: number;
      }>('/appointment/my_business/page', {
        params: {
          address: debouncedSearchValue,
          searchType: 'user',
          dateFrom: moment().format('YYYY-MM-DD'),
          sort_field: 'time',
          sort_asc: chronologicalShowingsEnabled ? 'true' : 'false',
          pageNumber: 0,
          preferences: ['new', 'attempted', 'confirm', 'time_change'],
          docType: ['virtualTour', 'appointment'],
        },
      });
      setListingAppointments(listingAppt);
    } catch (err) {
      errorReporter.error(err);
      message.error('Error loading appointments');
    } finally {
      setIsListingAppointmentsLoading(false);
    }
  };
  useEffect(() => {
    fetchAppointments();
  }, [debouncedSearchValue]);

  return {
    isListingAppointmentsLoading,
    listingAppointments,
    listingAppointmentsSearchValue: searchValue,
    setListingAppointmentsSearchValue: setSearchValue,
  };
};

export default useListingAppointments;
